import moment from 'moment';

// TODO(VHA): Use i18n
const timeSuffix = 'Uhr';

/**
 * Formats a date object with a given format.
 * @param {String} dateTime as a string
 * @param {boolean} ignoreInvalidDate specifies whether invalid dates should be returned as null (true) or not (false).
 * @param {String} format the format to be formatted by. Defaults to 'DD.MM.YYYY'.
 * @returns {String} the formatted date string
 */
function formatDate(dateTime, ignoreInvalidDate = false, format = 'DD.MM.YYYY') {
    // because JSON adds timezone information to the date, we need to parse it with moment.parseZone
    const dateTimeMoment = moment.parseZone(dateTime);
    if(dateTime === undefined || dateTime === null) {
        return null;
    }
    if (dateTimeMoment.isValid()) {
        if (dateTimeMoment.year() <= 1900) return '';
        return dateTimeMoment.format(format);
    } else {
        if (ignoreInvalidDate) {
            return null;
        }

        return '[invalid date]';
    }
}

/**
 * Formats a date object to a standardized (german) date-time string.
 * If the given {@link dateTime} does not have a reasonable day time,
 * the day time is omitted in the returned formatted string.
 * @param {String} dateTime dateTime as a string
 * @returns {string} the formatted (german) date-time string
 */
export function dateIsNull(dateTime) {
    return dateTime.startsWith('0001-01-01') ? null : dateTime;
}

/**
 * Formats a date object to a standardized (german) date-time string.
 * If the given {@link dateTime} does not have a reasonable day time,
 * the day time is omitted in the returned formatted string.
 * @param {String} dateTime dateTime as a string
 * @param {Boolean} needSeconds define, is it necessary to use seconds in end string
 * @param {Boolean} needSuffix define, is it necessary to use suffix in end string
 * @returns {string} the formatted (german) date-time string
 */
export function dateFormat(dateTime, needSeconds = false, needSuffix = true) {
    const dateTimeMoment = moment(dateTime);

    if (dateTimeMoment.isValid()) {
        if (dateTimeMoment.year() <= 1900) return '';
        let formatString = 'DD.MM.YYYY';
        let suffix = '';

        if (dateTimeMoment.format('HH:mm') !== '00:00') {
            formatString += (' - HH:mm' + (needSeconds ? ':ss' : ''));
            suffix = ` ${timeSuffix}`;
        }

        return dateTimeMoment.format(formatString) + (needSuffix ? suffix : '');
    } else {
        console.error(`invalid date object '${dateTime}'`);
        return '[invalid date]';
    }
}

/**
 * Formats a date object to a standardized date string.
 * @param {String} dateTime as a string
 * @param {boolean} ignoreInvalidDate specifies whether invalid dates should be returned as null (true) or not (false).
 * @returns {String} the formatted (german) date string
 */
export function dateFormatDay(dateTime, ignoreInvalidDate = false) {
    return formatDate(dateTime, ignoreInvalidDate);
}

/**
 * Formats a date object to a standardized technical date string.
 * @param {String} dateTime as a string
 * @param {boolean} ignoreInvalidDate specifies whether invalid dates should be returned as null (true) or not (false).
 * @returns {String} the formatted (technical) date string
 */
export function dateFormatDayTechnical(dateTime, ignoreInvalidDate = false) {
    return formatDate(dateTime, ignoreInvalidDate, 'YYYY-MM-DD');
}

/**
 * Formats a date object to a standardized technical datetime string.
 * @param {String} dateTime as a string
 * @param {boolean} ignoreInvalidDate specifies whether invalid dates should be returned as null (true) or not (false).
 * @returns {String} the formatted (technical) date string
 */
export function dateFormatTechnical(dateTime, ignoreInvalidDate = false) {
    return formatDate(dateTime, ignoreInvalidDate, 'YYYY-MM-DD HH:mm:ss');
}

/**
 * Formats a date object to a string consisting of spelled month and year.
 * @param {String} dateTime as a string
 * @returns {String} the formatted (german) date string
 */
export function dateFormatMonthYear(dateTime) {
    const dateTimeObject = new Date(dateTime);

    const month = dateTimeObject.toLocaleString('de', {month: 'long'});
    const year = dateTimeObject.getFullYear();

    return `${month} ${year}`;
}

/**
 * Removes the time substring if it is 00:00
 * @param {String} dateTime as a string
 */
export function removeDefaultTimeSubstring(dateTime) {
    return dateTime.replace(`- 00:00 ${timeSuffix}`, '');
}